<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null"
          @click="showCreateForm()"
        >
          New NCR
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="user.group.plants && user.group.plants.length != 0" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" expanded>
                <option v-for="plant in user.group.plants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
          </b-field>
          <ag-grid-vue
            v-if="ncr.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="ncr"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :active.sync="isCreatingNCR" scroll="keep" has-modal-card>
      <form v-if="isCreatingNCR" @submit.prevent="submitNCR">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Create NCR
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Date">
                <b-datepicker
                  v-model="newNCR.date"
                  name="newdate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="OB">
              <b-input v-model="newNCR.ob" type="text" />
            </b-field>
            <b-field label="Client">
              <multiselect
                v-model="newNCR.client"
                :options="customers"
                :multiple="false"
                :close-on-select="true"
                placeholder="Select Customer"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Site de destination">
              <multiselect
                v-model="newNCR.dest_site"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Site de provenance">
              <multiselect
                v-model="newNCR.prov_site"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Caisses Résponsabilité GPS">
              <b-input v-model="newNCR.resp_gps" type="number" />
            </b-field>
            <b-field label="Caisses Résponsabilité Client">
              <b-input v-model="newNCR.resp_client" type="number" />
            </b-field>
            <b-field label="Quantité totale">
              <b-input v-model="newNCR.quantity" type="number" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
    <b-modal :active.sync="isEditingNCR" scroll="keep" has-modal-card>
      <form v-if="isEditingNCR" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit NCR
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableNCR.date"
                  name="newdate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="OB">
              <b-input v-model="editableNCR.ob" type="text" />
            </b-field>
            <b-field label="Client">
              <multiselect
                v-model="editableNCR.client"
                :options="customers"
                :multiple="false"
                :close-on-select="true"
                placeholder="Select Customer"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Site de destination">
              <multiselect
                v-model="editableNCR.dest_site"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Site de provenance">
              <multiselect
                v-model="editableNCR.prov_site"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Caisses Résponsabilité GPS">
              <b-input v-model="editableNCR.resp_gps" type="number" />
            </b-field>
            <b-field label="Caisses Résponsabilité Client">
              <b-input v-model="editableNCR.resp_client" type="number" />
            </b-field>
            <b-field label="Quantité totale">
              <b-input v-model="editableNCR.quantity" type="number" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import { mapState } from 'vuex'
import Plant from '../../services/v2/plant'
import Customer from '@/services/v2/customer'
import NCRInspection from '../../services/v3/ncrInspection'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'NCRInspection',
  components: {
  },
  data: function () {
    return {
      editableNCR: null,
      newNCR: null,
      selectedPlant: JSON.parse(sessionStorage.getItem('consultingPlant')),
      isLoading: false,
      start: null,
      end: new Date(),
      isCreatingNCR: false,
      isEditingNCR: false,
      plants: [],
      ncr: [],
      customers: [],
      modules: AllModules,
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        columnDefs: [
          { headerName: 'Date',
            field: 'date',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'OB',
            field: 'ob',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Client',
            field: 'client',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            valueFormatter: (params) => {
              return params.value.id + ' - ' + params.value.name
            },
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Site de destination',
            field: 'dest_site',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            valueFormatter: (params) => {
              return params.value.id + ' - ' + params.value.name
            },
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Site de provenance',
            field: 'prov_site',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            valueFormatter: (params) => {
              return params.value.id + ' - ' + params.value.name
            },
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Caisses Résponsabilité GPS',
            field: 'resp_gps',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Caisses Résponsabilité Client',
            field: 'resp_client',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Quantité totale',
            field: 'quantity',
            filter: 'agTextColumnFilter',
            width: 160,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          }
        ],
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 30)
    this.plants = await Plant.getAll()
    this.customers = await Customer.getAll({})
  },
  methods: {
    resetData () {
      this.ncr = []
    },
    showCreateForm () {
      this.isCreatingNCR = true
      this.newNCR = {
        date: new Date(new Date().toLocaleDateString('en-CA')),
        ob: '',
        quantity: 0,
        client: '',
        dest_site: this.selectedPlant,
        prov_site: this.selectedPlant,
        resp_gps: 0,
        resp_client: 0,
        siteId: this.selectedPlant.id
      }
    },
    showEditForm (ncr) {
      this.isEditingNCR = true
      this.editableNCR = ncr
    },
    async getData () {
      if (!this.selectedPlant || !this.selectedPlant.id) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      this.isLoading = true
      try {
        const data = await NCRInspection.getTable({
          siteId: this.selectedPlant.id,
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA')
        })
        data.forEach(element => {
          this.ncr.push({
            id: element.id,
            id_site: element.id_site,
            ob: element.ob,
            date: element.date ? new Date(element.date.split('Z')[0]).toLocaleDateString('en-CA') : null,
            quantity: element.quantity,
            client: this.customers.filter(x => x.id === element.client) ? this.customers.filter(x => x.id === element.client)[0] : element.client,
            dest_site: this.plants.filter(x => x.id === element.dest_site) ? this.plants.filter(x => x.id === element.dest_site)[0] : element.dest_site,
            prov_site: this.plants.filter(x => x.id === element.prov_site) ? this.plants.filter(x => x.id === element.prov_site)[0] : element.prov_site,
            resp_gps: element.resp_gps,
            resp_client: element.resp_client
          })
        })
        if (this.ncr.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding NCR found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
        this.isLoading = false
      }
      this.isLoading = false
    },
    getContextMenuItems (params) {
      const selectedNCR = {
        date: new Date(new Date(params.node.data.date).toISOString().split('T')[0] + 'T00:00:00'),
        id: params.node.data.id,
        id_site: params.node.data.id_site,
        ob: params.node.data.ob,
        quantity: params.node.data.quantity,
        client: params.node.data.client,
        dest_site: params.node.data.dest_site,
        prov_site: params.node.data.prov_site,
        resp_gps: params.node.data.resp_gps,
        resp_client: params.node.data.resp_client
      }
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: 'Edit NCR',
        action: () => {
          this.showEditForm(selectedNCR)
        },
        icon: '<i class="fas fa-fw fa-edit" ></i>'
      })
      customMenu.push({
        name: 'Delete NCR',
        action: () => {
          this.deleteNC(selectedNCR)
        },
        icon: '<i class="fas fa-fw fa-xmark" ></i>'
      })
      const result = [...customMenu, ...genericMenu]
      return result
    },
    submitNCR () {
      this.isCreatingNCR = false
      try {
        NCRInspection.createNCR(
          {
            id_site: this.selectedPlant.id,
            ob: this.newNCR.ob,
            date: this.newNCR.date,
            quantity: this.newNCR.quantity,
            client: this.newNCR.client?.id,
            dest_site: this.newNCR.dest_site?.id,
            prov_site: this.newNCR.prov_site?.id,
            resp_gps: this.newNCR.resp_gps,
            resp_client: this.newNCR.resp_client
          }).then(() => {
          this.$buefy.toast.open({ message: 'NCR Created', duration: 3000, type: 'is-success' })
          this.resetData()
          this.getData()
        })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async submitEdit () {
      this.isEditingNCR = false
      try {
        await NCRInspection.updateNCR({
          id: this.editableNCR.id,
          id_site: this.editableNCR.id_site,
          ob: this.editableNCR.ob,
          date: this.editableNCR.date.toLocaleDateString('en-CA'),
          quantity: this.editableNCR.quantity,
          client: this.editableNCR.client?.id,
          dest_site: this.editableNCR.dest_site?.id,
          prov_site: this.editableNCR.prov_site?.id,
          resp_gps: this.editableNCR.resp_gps,
          resp_client: this.editableNCR.resp_client
        })
        this.$buefy.toast.open({ message: 'NCR Updated', duration: 3000, type: 'is-success' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteNC (ncr) {
      try {
        await NCRInspection.archiveNCR(ncr.id)
        this.$buefy.toast.open({ message: 'NCR Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>
